/* eslint-disable react-hooks/exhaustive-deps */
/// <reference types="@types/google.maps" />
import { useCallback, useEffect, useRef, useState } from "react";

type AddressAutoCompleteProps = {
  className?: string;
  placeholder: string;
  value?: string;
  onChange: (value: string) => void;
  onComplete: (value: string, addressComponents?: google.maps.GeocoderAddressComponent[]) => void;
};

const AddressAutoComplete = ({ onChange, onComplete, placeholder, value, className = "" }: AddressAutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [hasValidSelection, setHasValidSelection] = useState(false);
  const [selectionError, setSelectionError] = useState<string | undefined>();

  const onPlaceChanged = useCallback(() => {
    if (!autoCompleteRef.current) return;

    const place = autoCompleteRef.current.getPlace();
    if (!place || !place.formatted_address) return;

    onChange(place.formatted_address);
    setHasValidSelection(true);
    setSelectionError(undefined);
    onComplete(place.formatted_address, place.address_components || []);
  }, []);

  useEffect(() => {
    if (!inputRef.current) return;

    autoCompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
      types: ["geocode"],
      // Restrict autocomplete suggestions to addresses in the US and Canada.
      componentRestrictions: { country: ["us", "ca"] },
    });

    autoCompleteRef.current.addListener("place_changed", onPlaceChanged);

    return () => {
      if (autoCompleteRef.current) {
        google.maps.event.clearInstanceListeners(autoCompleteRef.current);
      }
    };
  }, []);

  // Initialize hasValidSelection only on mount if component receives existing saved address
  useEffect(() => {
    if (value) {
      setHasValidSelection(true);
    }
  }, []); // Empty dependency array - only run on mount

  const handleBlur = useCallback(() => {
    if (!value) {
      setSelectionError(undefined);
      onComplete("", undefined);
      return;
    }

    if (!hasValidSelection) {
      setSelectionError("Please select an address from the suggestions");
      onComplete(value, []);
      return;
    }

    setSelectionError(undefined);
  }, [value, hasValidSelection]);

  return (
    <div className="w-full">
      <input
        onBlur={handleBlur}
        ref={inputRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setHasValidSelection(false);
          setSelectionError(undefined);
        }}
        className={`focus:outline-none focus-visible:ring-0 ${className} ${selectionError ? "!border-orange-500 !bg-orange-50" : ""}`}
        type="text"
        placeholder={placeholder}
      />
      {selectionError && <p className="mt-1 text-xs text-red-500">{selectionError}</p>}
    </div>
  );
};

export default AddressAutoComplete;
